/**
 * Theme-token palette for inline-styled UI (onboarding row, /connections guide).
 * Every colour resolves through the app's CSS custom properties
 * (color-scheme-tokens.css — defined on `html, :host`, so it reaches the
 * extension's shadow root too) and therefore follows dark mode. The fallbacks
 * only matter if a token is ever missing.
 */
const SUCCESS = '#16a34a';

export const BRAND = {
  surface: 'var(--color-surface, #ffffff)',
  border: 'var(--color-border, #e5e7eb)',
  text: 'var(--color-text, #1a202c)',
  muted: 'var(--color-text-secondary, #5f6b7a)',
  accent: 'var(--color-accent, #53bdeb)',
  link: 'var(--color-link, #0ea5e9)',
  track: 'color-mix(in srgb, var(--color-text, #1a202c) 10%, transparent)',
  trackStrong: 'color-mix(in srgb, var(--color-text, #1a202c) 22%, transparent)',
  // The primary brand fill (FAB gradient) and its label — white in light, deep ink in dark.
  brand: 'var(--fab-bg, linear-gradient(66.67deg, #2B7FFF 0%, #51A2FF 100%))',
  brandHover: 'var(--fab-hover-bg, linear-gradient(66.67deg, #155DFC 0%, #2B7FFF 100%))',
  brandText: 'var(--fab-text, #ffffff)',
  brandShadow: '0 4px 12px -6px rgba(22, 119, 204, .6)',
  brandShadowHover: '0 10px 24px -10px rgba(22, 119, 204, .65)',
  success: SUCCESS,
  /** A soft corner wash of `color` over the surface. */
  wash: (color: string, pct = 10) =>
    `linear-gradient(90deg, color-mix(in srgb, ${color} ${pct}%, var(--color-surface, #ffffff)) 0%, var(--color-surface, #ffffff) 45%)`,
} as const;

/** A trailing `.bt-arrow` icon that nudges forward on hover and mirrors in RTL. */
export const arrowCss = (cls: string) => `
.${cls} .bt-arrow{transition:transform .15s ease}
.${cls}:hover .bt-arrow{transform:translateX(2px)}
[dir=rtl] .${cls} .bt-arrow{transform:scaleX(-1)}
[dir=rtl] .${cls}:hover .bt-arrow{transform:scaleX(-1) translateX(2px)}
`;

/** Shared CSS for brand-filled CTAs: lift on hover, keyboard focus ring, arrow.
 *  Scope it by passing the component's class prefix. */
export const brandCtaCss = (cls: string) => `
.${cls}{transition:transform .15s ease,box-shadow .15s ease}
.${cls}:hover:not(:disabled){transform:translateY(-1px);box-shadow:${BRAND.brandShadowHover};background:${BRAND.brandHover}!important}
.${cls}:active:not(:disabled){transform:none}
.${cls}:focus-visible{outline:2px solid ${BRAND.accent};outline-offset:2px}
${arrowCss(cls)}`;
